import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PhoneInput } from '@/components/ui/phone-input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, Edit, Trash2, Search, Warehouse, Key } from 'lucide-react';
import { toast } from 'sonner';
import { Pagination, ITEMS_PER_PAGE } from '@/components/Pagination';
import { TableSkeleton } from '@/components/ui/Skeletons';
import ConfirmDialog from '@/components/ui/ConfirmDialog';

const Users = ({ user }) => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState(['admin', 'manager', 'production_staff', 'staff']);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [warehouseDialogOpen, setWarehouseDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [warehouseEditingUser, setWarehouseEditingUser] = useState(null);
  const [warehouseLocations, setWarehouseLocations] = useState([]);
  const [selectedWarehouses, setSelectedWarehouses] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    email: '',
    password: '',
    role: 'staff',
  });
  const [countryCode, setCountryCode] = useState('+91');

  // Confirmation Dialog State
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Reset Password Dialog State
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetTargetUser, setResetTargetUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [isResetting, setIsResetting] = useState(false);

  const handleOpenResetPassword = (u) => {
    setResetTargetUser(u);
    setNewPassword('');
    setResetDialogOpen(true);
  };

  const handleResetPasswordSubmit = async (e) => {
    e.preventDefault();
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setIsResetting(true);
    try {
      await axios.put(`${API}/users/${resetTargetUser.id}/reset-password`, {
        new_password: newPassword
      });
      toast.success(`Password reset successfully for ${resetTargetUser.name}`);
      setResetDialogOpen(false);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setIsResetting(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchRoles();
    fetchWarehouseLocations();
  }, []);

  const fetchRoles = async () => {
    try {
      const res = await axios.get(`${API}/roles`);
      setRoles(res.data.roles.map(r => r.name));
    } catch { /* keep defaults */ }
  };

  const fetchWarehouseLocations = async () => {
    try {
      const res = await axios.get(`${API}/warehouse-locations`);
      setWarehouseLocations(res.data);
    } catch { /* ignore */ }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      mobile: '',
      email: '',
      password: '',
      role: 'staff',
    });
    setEditingUser(null);
  };

  const handleEdit = (u) => {
    setEditingUser(u);
    setFormData({
      name: u.name,
      mobile: u.mobile,
      email: u.email || '',
      password: '',
      role: u.role,
    });
    setDialogOpen(true);
  };



  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingUser) {
        await axios.put(`${API}/users/${editingUser.id}`, {
          name: formData.name,
          role: formData.role,
        });
        toast.success('User updated successfully');
      } else {
        await axios.post(`${API}/auth/register`, {
          mobile: `${countryCode}${formData.mobile}`,
          email: formData.email || null,
          password: formData.password,
          name: formData.name,
          role: formData.role,
        });
        toast.success('User created successfully');
      }

      setDialogOpen(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'An error occurred');
    }
  };



  const handleEditWarehouses = (u) => {
    setWarehouseEditingUser(u);
    setSelectedWarehouses(u.assigned_warehouses || []);
    setWarehouseDialogOpen(true);
  };

  const toggleWarehouse = (name) => {
    setSelectedWarehouses(prev =>
      prev.includes(name) ? prev.filter(w => w !== name) : [...prev, name]
    );
  };

  const handleSaveWarehouses = async () => {
    try {
      await axios.put(`${API}/users/${warehouseEditingUser.id}/warehouses`, {
        warehouse_names: selectedWarehouses
      });
      toast.success('Warehouses assigned successfully');
      setWarehouseDialogOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign warehouses');
    }
  };

  const handleDeleteClick = (userItem) => {
    setItemToDelete(userItem);
    setConfirmOpen(true);
  };

  const executeDelete = async () => {
    if (!itemToDelete) return;
    setIsDeleting(true);
    try {
      await axios.delete(`${API}/users/${itemToDelete.id}`);
      toast.success('User deleted successfully');
      fetchUsers();
      setConfirmOpen(false);
    } catch (error) {
      toast.error('Failed to delete user');
    } finally {
      setIsDeleting(false);
      setItemToDelete(null);
    }
  };



  if (loading) {
    return (
      <div className="p-8 space-y-6">
        <div className="flex justify-between mb-8">
          <div>
            <div className="h-10 w-48 bg-stone-200/60 rounded animate-pulse mb-2"></div>
            <div className="h-4 w-64 bg-stone-200/60 rounded animate-pulse"></div>
          </div>
          <div className="h-10 w-32 bg-stone-200/60 rounded animate-pulse"></div>
        </div>
        <div className="bg-white p-6 rounded-sm border border-stone-200 shadow-sm">
          <TableSkeleton rows={5} columns={6} />
        </div>
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="users-page">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-4xl font-semibold text-[hsl(145,60%,10%)] tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>User Management</h1>
          <p className="text-sm text-[hsl(145,20%,40%)] mt-2">Manage users and warehouse assignments</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-[hsl(145,60%,20%)] hover:bg-[hsl(145,60%,15%)] text-white" data-testid="add-user-button">
              <Plus size={16} className="mr-2" />
              Add User
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingUser ? 'Edit User' : 'Add New User'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="user-form">
              <div>
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="input-name"
                />
              </div>
              <div>
                <Label htmlFor="mobile">Mobile Number</Label>
                <PhoneInput
                  value={formData.mobile}
                  onChange={(val) => setFormData({ ...formData, mobile: val })}
                  countryCode={countryCode}
                  onCountryCodeChange={setCountryCode}
                  required={!editingUser}
                  disabled={!!editingUser}
                />
              </div>
              <div>
                <Label htmlFor="email">Email (Optional)</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  disabled={!!editingUser}
                  placeholder="Enter email address"
                  data-testid="input-email"
                />
              </div>
              {!editingUser && (
                <div>
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required={!editingUser}
                    data-testid="input-password"
                  />
                </div>
              )}
              <div>
                <Label htmlFor="role">Role</Label>
                <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                  <SelectTrigger data-testid="select-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {roles.map(role => (
                      <SelectItem key={role} value={role}>{role.replace('_', ' ').toUpperCase()}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-[hsl(145,20%,40%)] mt-1">
                  Permissions are managed through the Roles page
                </p>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button type="submit" className="bg-[hsl(145,60%,20%)] hover:bg-[hsl(145,60%,15%)] text-white" data-testid="submit-user-button">
                  {editingUser ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[hsl(145,20%,40%)]" size={18} />
          <Input
            placeholder="Search by name, mobile, or role..."
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            className="pl-10"
          />
        </div>
      </div>

      <div className="bg-white rounded-sm border border-stone-200 shadow-sm overflow-hidden mb-4">
        {/* Desktop Table View */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full" data-testid="users-table">
            <thead className="bg-stone-50 border-b border-stone-100">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-bold text-stone-600 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-bold text-stone-600 uppercase tracking-wider">Mobile</th>
                <th className="px-6 py-3 text-left text-xs font-bold text-stone-600 uppercase tracking-wider">Email</th>
                <th className="px-6 py-3 text-left text-xs font-bold text-stone-600 uppercase tracking-wider">Role</th>
                <th className="px-6 py-3 text-left text-xs font-bold text-stone-600 uppercase tracking-wider">Warehouses</th>
                <th className="px-6 py-3 text-left text-xs font-bold text-stone-600 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {(() => {
                const filtered = users.filter(u => {
                  if (!searchTerm) return true;
                  const q = searchTerm.toLowerCase();
                  return u.name.toLowerCase().includes(q) || u.mobile.toLowerCase().includes(q) || u.role.toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
                });
                const paginated = filtered.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
                return paginated.map((u) => (
                  <tr key={u.id} className="hover:bg-stone-50/50 transition-colors" data-testid={`user-row-${u.mobile}`}>
                    <td className="px-6 py-2 text-sm text-[hsl(145,60%,10%)] font-medium">{u.name}</td>
                    <td className="px-6 py-2 text-sm text-[hsl(145,60%,10%)]">{u.mobile}</td>
                    <td className="px-6 py-2 text-sm text-[hsl(145,20%,40%)]">{u.email || '-'}</td>
                    <td className="px-6 py-2 text-sm">
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${u.role === 'admin' ? 'bg-purple-100 text-purple-700' :
                        u.role === 'manager' ? 'bg-blue-100 text-blue-700' :
                          u.role === 'production_staff' ? 'bg-green-100 text-green-700' :
                            'bg-gray-100 text-gray-700'
                        }`}>
                        {u.role.replace('_', ' ').toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-2 text-sm text-[hsl(145,20%,40%)]">
                      {(u.assigned_warehouses && u.assigned_warehouses.length > 0)
                        ? <div className="flex flex-wrap gap-1">
                          {u.assigned_warehouses.map(w => (
                            <span key={w} className="px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded text-xs font-medium">{w}</span>
                          ))}
                        </div>
                        : <span className="text-stone-400 italic">All</span>
                      }
                    </td>
                    <td className="px-6 py-2 text-sm">
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(u)} title="Edit User" data-testid={`edit-user-${u.mobile}`}>
                          <Edit size={14} />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleEditWarehouses(u)} title="Assign Warehouses" data-testid={`warehouse-user-${u.mobile}`}>
                          <Warehouse size={14} className="text-emerald-600" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleOpenResetPassword(u)} title="Reset Password" data-testid={`reset-password-user-${u.mobile}`}>
                          <Key size={14} className="text-amber-600" />
                        </Button>
                        {u.id !== user.id && (
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteClick(u)} title="Delete User" data-testid={`delete-user-${u.mobile}`}>
                            <Trash2 size={14} className="text-red-600" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ));
              })()}
            </tbody>
          </table>
        </div>

        {/* Mobile Card View */}
        <div className="md:hidden flex flex-col divide-y divide-stone-100">
          {(() => {
            const filtered = users.filter(u => {
              if (!searchTerm) return true;
              const q = searchTerm.toLowerCase();
              return u.name.toLowerCase().includes(q) || u.mobile.toLowerCase().includes(q) || u.role.toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
            });
            return filtered.length === 0 ? (
              <div className="p-8 text-center text-[hsl(145,20%,40%)]">No users found</div>
            ) : filtered.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE).map((u) => (
              <div key={`mobile-${u.id}`} className="p-4 bg-white">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h4 className="font-semibold text-stone-900">{u.name}</h4>
                    <p className="text-sm text-stone-500">{u.mobile}</p>
                    {u.email && <p className="text-xs text-stone-400 mt-0.5">{u.email}</p>}
                  </div>
                  <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-medium ${u.role === 'admin' ? 'bg-purple-100 text-purple-700' :
                    u.role === 'manager' ? 'bg-blue-100 text-blue-700' :
                      u.role === 'production_staff' ? 'bg-green-100 text-green-700' :
                        'bg-gray-100 text-gray-700'
                    }`}>
                    {u.role.replace('_', ' ').toUpperCase()}
                  </span>
                </div>

                <div className="mt-3">
                  <span className="text-xs text-stone-500 block mb-1">Assigned Warehouses</span>
                  {(u.assigned_warehouses && u.assigned_warehouses.length > 0) ? (
                    <div className="flex flex-wrap gap-1">
                      {u.assigned_warehouses.map(w => (
                        <span key={w} className="px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded border border-blue-100 text-[10px] font-medium">{w}</span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-stone-400 text-xs italic">All Warehouses</span>
                  )}
                </div>

                <div className="flex gap-2 justify-end mt-4 pt-3 border-t border-stone-100">
                  <Button variant="outline" size="sm" className="h-8 text-xs text-emerald-600 flex-1" onClick={() => handleEditWarehouses(u)}>
                    <Warehouse size={12} className="mr-1.5" /> Warehouses
                  </Button>
                  <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => handleEdit(u)}>
                    <Edit size={14} className="text-stone-600" />
                  </Button>
                  {u.id !== user.id && (
                    <Button variant="outline" size="sm" className="h-8 w-8 p-0 border-red-200 hover:bg-red-50" onClick={() => handleDeleteClick(u)}>
                      <Trash2 size={14} className="text-red-500" />
                    </Button>
                  )}
                </div>
              </div>
            ));
          })()}
        </div>
      </div>
      <div className="flex justify-center">
        <Pagination currentPage={currentPage} totalItems={users.filter(u => { if (!searchTerm) return true; const q = searchTerm.toLowerCase(); return u.name.toLowerCase().includes(q) || u.mobile.toLowerCase().includes(q) || u.role.toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q); }).length} onPageChange={setCurrentPage} />
      </div>

      <ConfirmDialog
        isOpen={confirmOpen}
        onOpenChange={setConfirmOpen}
        onConfirm={executeDelete}
        title={`Delete User: ${itemToDelete?.name}?`}
        description="This action cannot be undone. This user will be permanently removed from the system."
        confirmText="Delete User"
        isLoading={isDeleting}
      />

      {/* Warehouse Assignment Dialog */}
      <Dialog open={warehouseDialogOpen} onOpenChange={setWarehouseDialogOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Warehouse size={20} />
              Assign Warehouses: {warehouseEditingUser?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            <p className="text-sm text-[hsl(145,20%,40%)] mb-4">
              Select the warehouse locations this user should have access to. If none are selected, the user can see all warehouses.
            </p>
            {warehouseLocations.length === 0 ? (
              <p className="text-sm text-stone-400 text-center py-4">No warehouse locations found. Create them in Warehouse Locations page first.</p>
            ) : (
              <div className="space-y-2">
                {warehouseLocations.map(loc => (
                  <label
                    key={loc.id}
                    className={`flex items-center gap-3 p-3 rounded border cursor-pointer transition-all ${selectedWarehouses.includes(loc.name)
                      ? 'border-emerald-300 bg-emerald-50'
                      : 'border-stone-200 bg-white hover:border-stone-300'
                      }`}
                  >
                    <Checkbox
                      checked={selectedWarehouses.includes(loc.name)}
                      onCheckedChange={() => toggleWarehouse(loc.name)}
                      data-testid={`warehouse-checkbox-${loc.name}`}
                    />
                    <div>
                      <span className="text-sm font-medium text-[hsl(145,60%,10%)]">{loc.name}</span>
                      {loc.description && (
                        <p className="text-xs text-[hsl(145,20%,40%)]">{loc.description}</p>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            )}
            <div className="flex justify-between items-center mt-6">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedWarehouses([])}
                className="text-stone-500"
              >
                Clear All
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setWarehouseDialogOpen(false)}>Cancel</Button>
                <Button
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  onClick={handleSaveWarehouses}
                  data-testid="save-warehouses-button"
                >
                  <Warehouse size={14} className="mr-2" />
                  Save Assignments
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key size={20} className="text-amber-600" />
              Reset Password for {resetTargetUser?.name}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleResetPasswordSubmit} className="space-y-4 mt-2">
            <div>
              <Label htmlFor="new-password">New Password</Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password (min 6 chars)"
                required
                className="mt-1"
                data-testid="input-new-password"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setResetDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-amber-600 hover:bg-amber-700 text-white"
                disabled={isResetting}
                data-testid="submit-reset-password-button"
              >
                {isResetting ? 'Resetting...' : 'Reset Password'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Users;
